import React from 'react';

import {assert} from 'Asserts';
import {DataFrame} from 'data-frames/DataFrameTypes';
import DataFrameTableEditable from 'data-frames/editable-table/DataFrameTableEditable';
import {DEFAULT_DATA_FRAME_ROW_LIMIT} from 'data-frames/graphql/GetDataFrame';
import {
  GetPersistedDataFrame,
  GetPersistedDataFrameVariables,
} from 'data-frames/wrapper/__generated/GetPersistedDataFrame';
import {GET_PERSISTED_DATA_FRAME_QUERY} from 'data-frames/wrapper/PersistedDataFrameTableWrapper';
import BenchlingQuery from 'graphql-client/BenchlingQuery';
import {assertIsAccessible} from 'graphql-client/InaccessibleInfo';
import {GetPipelineConfigSpecType} from 'pipelines/StepShapeUtils';
import DataTestAttrs from 'test/lib/cypress/shared/DataTestAttrs';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';
import {JSONValue} from 'util/Types';

import {useStepConfigEditorContext} from '../StepConfigEditorContext';

export interface TableReferenceEditorProps {
  config: Array<Record<string, JSONValue>>;
  configShape: GetPipelineConfigSpecType<'tableReference'>;
  dataFrame: DataFrame;
  disabled?: boolean;
  disabledColumns?: Array<string>;
  hiddenColumns?: Array<string>;
  onChangeConfig: (config: Array<Record<string, JSONValue>>) => void;
  dataTestAttrs?: DataTestAttrs;
}

export function TableReferenceEditor({
  config,
  dataFrame,
  onChangeConfig,
  disabled,
}: TableReferenceEditorProps): JSX.Element {
  const {dataFrameId, clientStateId} = useStepConfigEditorContext();
  assert(dataFrameId != null, 'Using TableReferenceEditor in context without access to persisted data frame');

  return (
    <DataFrameTableEditable
      originalDataFrame={dataFrame}
      dataFrameRowsOverride={config.length > 0 ? config : dataFrame.rows}
      clientStateId={`TableReferenceEditor-${clientStateId}-${dataFrameId}`}
      onUpdateDataFrameRows={onChangeConfig}
      disabled={disabled}
    />
  );
}

/** @package */
export type QueryingTableReferenceEditorProps = Omit<TableReferenceEditorProps, 'dataFrame'>;

export default function QueryingTableReferenceEditor(props: QueryingTableReferenceEditorProps): JSX.Element {
  const {dataFrameId} = useStepConfigEditorContext();
  assert(dataFrameId != null, 'Using TableReferenceEditor in context without access to persisted data frame');

  return (
    <BenchlingQuery<GetPersistedDataFrame, GetPersistedDataFrameVariables>
      query={GET_PERSISTED_DATA_FRAME_QUERY}
      renderLoading={() => <CenteredHorizontalThrobber />}
      variables={{dataFrameId, limit: DEFAULT_DATA_FRAME_ROW_LIMIT}}
    >
      {({dataFrame: persistedDataFrame}) => (
        <TableReferenceEditor {...props} dataFrame={assertIsAccessible(persistedDataFrame).dataFrameJson} />
      )}
    </BenchlingQuery>
  );
}
